import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';
import { get } from 'aws-amplify/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, AlertCircle, Clock } from 'lucide-react';

interface QueueMetrics {
  queueName: string;
  queueUrl: string;
  approximateNumberOfMessages: number;
  approximateNumberOfMessagesNotVisible: number;
  approximateNumberOfMessagesDelayed: number;
  deadLetterQueueUrl?: string;
  deadLetterQueueMessages?: number;
  queueType: 'standard' | 'fifo' | 'deadletter';
}

export default function Queues() {
  const { isAuthenticated, session } = useAuth();
  const { toast } = useToast();
  const [queues, setQueues] = useState<QueueMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchQueueData();
    }
  }, [isAuthenticated]);

  const fetchQueueData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = get({
        apiName: 'dashboard',
        path: '/data/queues',
        options: {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.tokens?.idToken}`
          },
          withCredentials: true
        }
      });

      const { body } = await response.response;
      const data = await body.json();
      
      setQueues(data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching queue data:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch queue data');
      
      toast({
        title: "Error",
        description: "Failed to fetch queue data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTotalMessages = () => {
    return queues.reduce((total, queue) => 
      total + queue.approximateNumberOfMessages + (queue.deadLetterQueueMessages || 0), 0
    );
  };

  const getQueueTypeColor = (type: string) => {
    switch (type) {
      case 'deadletter':
        return 'destructive';
      case 'fifo':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getMessageCountColor = (count: number) => {
    if (count === 0) return 'text-green-600';
    if (count < 10) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (isLoading && !queues.length) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <LoadingSpinner size="lg" message="Loading queue data..." />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">SQS Queues</h1>
          <p className="text-muted-foreground">
            Monitor message queues and dead-letter queues
          </p>
        </div>
        <Button
          onClick={fetchQueueData}
          disabled={isLoading}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {lastRefresh && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          Last updated: {lastRefresh.toLocaleTimeString()}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Queues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{queues.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getMessageCountColor(getTotalMessages())}`}>
              {getTotalMessages()}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Dead Letter Queues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {queues.filter(q => q.queueType === 'deadletter').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {error ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p className="font-medium">Error loading queue data</p>
            </div>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {queues.map((queue) => (
            <Card key={queue.queueUrl}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{queue.queueName}</CardTitle>
                  <Badge variant={getQueueTypeColor(queue.queueType)}>
                    {queue.queueType.toUpperCase()}
                  </Badge>
                </div>
                <CardDescription className="font-mono text-xs break-all">
                  {queue.queueUrl}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Available Messages</p>
                    <p className={`text-2xl font-bold ${getMessageCountColor(queue.approximateNumberOfMessages)}`}>
                      {queue.approximateNumberOfMessages}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium">In-Flight Messages</p>
                    <p className={`text-2xl font-bold ${getMessageCountColor(queue.approximateNumberOfMessagesNotVisible)}`}>
                      {queue.approximateNumberOfMessagesNotVisible}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Delayed Messages</p>
                    <p className={`text-2xl font-bold ${getMessageCountColor(queue.approximateNumberOfMessagesDelayed)}`}>
                      {queue.approximateNumberOfMessagesDelayed}
                    </p>
                  </div>
                </div>
                
                {queue.deadLetterQueueUrl && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-red-700">Dead Letter Queue Messages</p>
                      <p className={`text-lg font-bold ${getMessageCountColor(queue.deadLetterQueueMessages || 0)}`}>
                        {queue.deadLetterQueueMessages || 0}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono break-all">
                      {queue.deadLetterQueueUrl}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          
          {queues.length === 0 && !isLoading && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">No queues found</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}